import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type Article = {
  slug: string;
  key: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  thumbnailUrl: string | null;
};

export default function Blog() {
  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: 'Blog — Samia Systems',
    description:
      'Field notes on engineering-grade AI automation for professional services firms.',
  });

  useEffect(() => {
    let alive = true;
    fetch('/api/articles')
      .then(async (r) => {
        if (!r.ok && r.status !== 503) return { items: [] };
        return r.json() as Promise<{ items: Article[] }>;
      })
      .then((data) => {
        if (!alive) return;
        setItems(data.items ?? []);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setItems([]);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <section className="grain relative bg-black px-6 pb-12 pt-40">
        <div className="container-x">
          <p className="label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Blog
          </p>
          <h1 className="max-w-4xl font-display text-hero font-light text-white text-balance">
            Field notes. <span className="italic text-gold">Specific.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-sans text-base text-white/85">
            What we learn on the way. Each article documents a real engineering
            decision — what we picked, what we ruled out, what surprised us.
          </p>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x">
          {loading && (
            <p className="font-mono text-[0.7rem] uppercase tracking-label text-stone">
              Loading…
            </p>
          )}

          {!loading && items.length === 0 && (
            <div className="rounded-4xl border border-white/10 bg-charcoal p-12 text-center">
              <p className="font-display text-2xl font-light text-white">
                Nothing published yet.
              </p>
              <p className="mt-3 font-sans text-body text-white/80">
                The first article goes up shortly.
              </p>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2">
              {items.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="group relative flex flex-col overflow-hidden rounded-4xl border border-white/10 bg-charcoal transition-colors hover:border-gold/40"
                >
                  {a.thumbnailUrl ? (
                    <div className="aspect-[16/9] w-full overflow-hidden border-b border-white/5 bg-black">
                      <img
                        src={a.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
                      />
                    </div>
                  ) : (
                    <div className="grain aspect-[16/9] w-full border-b border-white/5 bg-black" />
                  )}
                  <div className="flex flex-1 flex-col justify-between p-7">
                    <div>
                      <div className="flex items-center justify-between font-mono text-[0.65rem] uppercase tracking-label text-stone">
                        <span>{a.date}</span>
                        <span>{a.author}</span>
                      </div>
                      <h2 className="mt-6 font-display text-2xl font-light leading-tight text-white">
                        {a.title}
                      </h2>
                      {a.excerpt && (
                        <p className="mt-3 font-sans text-body text-white/80">
                          {a.excerpt}
                        </p>
                      )}
                    </div>
                    <span className="mt-8 inline-flex items-center gap-2 border-t border-white/5 pt-5 font-sans text-[0.7rem] uppercase tracking-label text-gold transition-transform group-hover:translate-x-1">
                      Read <span aria-hidden>→</span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
